/**
 * Campo único de posologia do receituário estruturado.
 *
 * Substitui os quatro campos separados (dose, frequência, via e duração) por
 * uma frase só. O texto é reconvertido nas colunas estruturadas a cada
 * digitação, de modo que impressão, modelos e histórico continuam iguais.
 *
 * O valor exibido só é reescrito a partir do item quando `syncKey` muda — é o
 * que permite o preenchimento automático pela CMED sem atropelar quem digita.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, InputAdornment, TextField, Tooltip } from '@mui/material';
import { VerifiedOutlined as VerifiedIcon } from '@mui/icons-material';
import {
  PosologyLineFields,
  composePosologyLine,
  parsePosologyLine,
} from '../../utils/prescriptionPosologyLine';

export interface MedicationPosologyLineFieldProps {
  item: {
    dosage?: string | null;
    frequency?: string | null;
    route?: string | null;
    duration?: string | null;
  };
  /** Muda quando a linha deve ser reescrita a partir do item (troca de medicamento, autofill). */
  syncKey: string;
  onChange: (fields: PosologyLineFields) => void;
  loading?: boolean;
  verified?: boolean;
  label?: string;
  helperText?: React.ReactNode;
}

const MedicationPosologyLineField: React.FC<MedicationPosologyLineFieldProps> = ({
  item,
  syncKey,
  onChange,
  loading = false,
  verified = false,
  label = 'Posologia (dose, frequência, via e duração)',
  helperText,
}) => {
  const [draft, setDraft] = useState(() => composePosologyLine(item));
  const lastSyncKey = useRef(syncKey);

  useEffect(() => {
    if (lastSyncKey.current === syncKey) return;
    lastSyncKey.current = syncKey;
    setDraft(composePosologyLine(item));
  }, [syncKey, item]);

  const handleChange = (value: string) => {
    setDraft(value);
    onChange(parsePosologyLine(value));
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={draft}
      onChange={(event) => handleChange(event.target.value)}
      placeholder="Ex.: Tomar 1 comprimido, de 12 em 12 horas, via oral, durante 7 dias"
      helperText={helperText}
      FormHelperTextProps={helperText ? { sx: { mt: 0.25, mx: 0 } } : undefined}
      sx={{ '& .MuiFormHelperText-root': { lineHeight: 1.2 } }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {loading && <CircularProgress size={16} />}
            {!loading && verified && (
              <Tooltip title="Preenchido pela bula profissional vigente da ANVISA">
                <Box sx={{ display: 'flex' }}>
                  <VerifiedIcon fontSize="small" color="success" />
                </Box>
              </Tooltip>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default MedicationPosologyLineField;
